import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { CompanyLogo } from "./CompanyLogo";
import { BasicInformation } from "./BasicInformation";
import { AddressInformation } from "./AddressInformation";
import { NotesField } from "./NotesField";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Form } from "@/components/ui/form";
import { toast } from "@/components/ui/use-toast";

const companyFormSchema = z.object({
  id: z.string(),
  name: z.string().min(2, {
    message: "Company name must be at least 2 characters"
  }),
  street: z.string().optional(),
  postal_code: z.string().optional(),
  city: z.string().optional(),
  country: z.string().default("Belgium"),
  assignee: z.string().optional(),
  notes: z.string().optional(),
  vat_number: z.string().optional(),
  logo_url: z.string().optional().nullable()
});

export type CompanyFormValues = z.infer<typeof companyFormSchema>;

interface Company {
  id: string;
  name: string;
  street?: string | null;
  postal_code?: string | null;
  city?: string | null;
  country?: string | null;
  assignee?: string | null;
  notes?: string | null;
  vat_number?: string | null;
  logo_url?: string | null;
}

interface EditCompanyModalProps {
  company: Company | null;
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: CompanyFormValues) => Promise<boolean>;
}

export const EditCompanyModal = ({
  company,
  isOpen,
  onClose,
  onSubmit
}: EditCompanyModalProps) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const form = useForm<CompanyFormValues>({
    resolver: zodResolver(companyFormSchema),
    defaultValues: {
      id: company?.id || "",
      name: company?.name || "",
      street: company?.street || "",
      postal_code: company?.postal_code || "",
      city: company?.city || "",
      country: company?.country || "Belgium",
      assignee: company?.assignee || "",
      notes: company?.notes || "",
      vat_number: company?.vat_number || "",
      logo_url: company?.logo_url || ""
    }
  });

  useEffect(() => {
    if (company && isOpen) {
      form.reset({
        id: company.id,
        name: company.name,
        street: company.street || "",
        postal_code: company.postal_code || "",
        city: company.city || "",
        country: company.country || "Belgium",
        assignee: company.assignee || "",
        notes: company.notes || "",
        vat_number: company.vat_number || "",
        logo_url: company.logo_url || ""
      });
    }
  }, [company, form, isOpen]);

  const handleSubmit = async (values: CompanyFormValues) => {
    setIsSubmitting(true);
    try {
      const success = await onSubmit(values);
      if (success) {
        onClose();
        toast({
          title: "Success",
          description: "Company updated successfully"
        });
      }
    } catch (error) {
      console.error("Error updating company:", error);
      toast({
        title: "Error",
        description: "Failed to update company",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const clearLogoUrl = () => {
    form.setValue("logo_url", "");
  };
  
  const handleLogoChange = (logoUrl: string | undefined) => {
    form.setValue("logo_url", logoUrl || "");
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="w-full max-w-[1200px] p-6 overflow-y-auto">
        <DialogHeader className="mb-6">
          <DialogTitle>Edit Company</DialogTitle>
          <DialogDescription>
            Update company information.
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6">
            <CompanyLogo logoUrl={form.watch("logo_url") || ""} companyName={form.watch("name")} onClearLogo={clearLogoUrl} />
            
            <BasicInformation 
              name={form.watch("name")} 
              vatNumber={form.watch("vat_number") || ""} 
              assignee={form.watch("assignee") || ""} 
              logoUrl={form.watch("logo_url") || ""}
              onChange={e => {
                form.setValue(e.target.id as any, e.target.value);
              }}
              onLogoChange={handleLogoChange}
            />
            
            <AddressInformation street={form.watch("street") || ""} postalCode={form.watch("postal_code") || ""} city={form.watch("city") || ""} country={form.watch("country") || ""} onChange={e => {
              form.setValue(e.target.id as any, e.target.value);
            }} />
            
            <NotesField notes={form.watch("notes") || ""} onChange={e => {
              form.setValue("notes", e.target.value);
            }} />

            <div className="flex justify-end gap-3 mt-8 pt-4 border-t">
              <Button type="button" variant="outline" onClick={onClose}>
                Cancel
              </Button>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? "Updating..." : "Update Company"}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
